import React, {useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {CtaButton} from '@optimaxdev/design-system';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_DELIVERY_STEP} from 'constants/analytics/events/checkout';
import {PRIVACY_POLICY, TERMS_AND_CONDITIONS} from 'constants/cmsContent';
import {openPopupWithCMSBlock} from 'desktop/reducers/cmsPopup';
import {ApplicationStoreType} from 'desktop/store';
import {AddressType} from 'desktop/types/addressType';
import {useSelectorRef} from 'hooks/useSelectorRef/useSelectorRef';

import {setPayerOptions} from '../../store/reducers/paymentStep/payerInfo/payerInfo';
import {setSubscribeCheckboxStatus} from '../../store/reducers/sagaStep/sagaStep';
import {getContinueButtonIsDisabled} from '../../store/selectors/deliveryStep/deliveryStep';
import {
    getEmailSubscriptionVisibility,
    getSmsSubscriptionVisibility,
} from '../../store/selectors/newsletterSubscription/newsletterSubscription';
import {getPayerInfo} from '../../store/selectors/paymentStep/paymentStep';
import {getShippingAddress} from '../../store/selectors/shippingAddress/shippingAddress';
import {getShippingMethodsHits} from '../../store/selectors/shippingMethods/shippingMethods';
import {DeliveryStepError} from '../deliveryStepError/deliveryStepError';

import {SubscriptionModal} from '../subscriptionModal/subscriptionModal';

import {sendNonUSStateAnalytic, sendUSStateAnalytic} from './stateAnalytic';

import s from './deliveryFormContinueButton.scss';

export type PropsType = {
    isDisabled: boolean;
    openCmsPopup: (data: {cmsBlockName: string; checkoutStep: string}) => void;
    isCheckboxShown: boolean;
    shippingAddress: AddressType;
    isSubscriptionPopupEnabled: boolean;
    closeSubscriptionPopup: () => void;
    handleSubmit: () => void;
};

/**
 * Delivery Form Continue Button for checkout
 *
 * @constructor
 */
export const DeliveryFormContinueButton: React.FC<PropsType> = ({
    closeSubscriptionPopup,
    handleSubmit,
    isCheckboxShown,
    isDisabled,
    isSubscriptionPopupEnabled,
    openCmsPopup,
    shippingAddress,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {country, state} = shippingAddress;
    const shippingHitsRef = useSelectorRef(getShippingMethodsHits);
    const isUsCountry = country.value === 'US';

    const openTermsOfUse = () => {
        openCmsPopup({checkoutStep: 'DELIVERY_STEP', cmsBlockName: TERMS_AND_CONDITIONS});

        sendDesktopAnalyticsEvent(CHECKOUT_DELIVERY_STEP, {
            message: 'Terms & Condition - opened',
        });
    };

    const openPrivacyPolicy = () => {
        openCmsPopup({checkoutStep: 'DELIVERY_STEP', cmsBlockName: PRIVACY_POLICY});

        sendDesktopAnalyticsEvent(CHECKOUT_DELIVERY_STEP, {message: 'Privacy Policy - opened'});
    };

    useEffect(() => {
        if (isUsCountry) sendUSStateAnalytic(String(state.value), shippingHitsRef.current);
    }, [isUsCountry, state.value, shippingHitsRef]);

    useEffect(() => {
        if (!isUsCountry) sendNonUSStateAnalytic(shippingHitsRef.current);
    }, [isUsCountry, shippingHitsRef]);

    return (
        <section
            className={classNames(s.wrapperRedesign, {
                [s.checkShown]: isCheckboxShown,
                [s.wrapperFacelift]: isFacelift,
            })}
        >
            <div className={classNames(s.buttonWrapRedesign, {[s.buttonWrapFacelift]: isFacelift})}>
                <CtaButton
                    className={s.buttonRedesign}
                    isDisabled={isDisabled}
                    size="large"
                    type="submit"
                >
                    Continue
                </CtaButton>
                <DeliveryStepError />
            </div>
            <SubscriptionModal
                closePopup={closeSubscriptionPopup}
                handleSubmit={handleSubmit}
                isOpen={isSubscriptionPopupEnabled}
            />

            {!isCheckboxShown && (
                <p className={classNames(s.textRedesign, {[s.textFacelift]: isFacelift})}>
                    By continuing you agree to our{' '}
                    <button className={s.link} onClick={openTermsOfUse} type="button">
                        Terms of Use
                    </button>{' '}
                    &{' '}
                    <button className={s.link} onClick={openPrivacyPolicy} type="button">
                        Privacy Policy
                    </button>
                    .
                </p>
            )}
        </section>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    isCheckboxShown: getEmailSubscriptionVisibility(store) || getSmsSubscriptionVisibility(store),
    isChecked: Boolean(getPayerInfo(store).options.subscribe),
    isDisabled: getContinueButtonIsDisabled(store),
    shippingAddress: getShippingAddress(store),
});

const mapDispatchToProps = {
    changeSubscribeOption: setPayerOptions,
    openCmsPopup: openPopupWithCMSBlock,
    setSubscribtionCheckbox: setSubscribeCheckboxStatus,
};

export const DeliveryFormContinueButtonConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(DeliveryFormContinueButton);
